import React, { useRef, useState, useEffect } from "react";
import { useBreakpoint } from "../../../../../../common-deprecated/themes/common";
import { useCommonSelector } from "../../../../../../common-deprecated/redux/commonStore";
import { useWindowDimensions } from "../../../../../../common-deprecated/hooks";
import useSlider from "../../../../../../common-deprecated/hooks/useSlider";
import { getTyreLabelsUrl } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import Button from "../../../styles/atoms/Button";
import Icon from "../../../styles/atoms/Icon";
import * as Styles from "./TyreSlider.styles";
import type { TyreInfoLabels } from "../../../../../../shared-logic/utils/tyreInfoContants";

type TyreSliderProps = { tyres: string[]; tyreInfoLabels: TyreInfoLabels };
const TyreSlider = ({ tyres, tyreInfoLabels }: TyreSliderProps): JSX.Element => {
    const settings = useCommonSelector((state) => state.commonSettings);

    const viewPortRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const windowWidth = useWindowDimensions().width;

    const [viewPortWidth, setViewPortWidth] = useState<number>(0);
    const [tyreLabelError, setTyreLabelError] = useState<boolean>(false);

    useEffect(() => {
        if (viewPortRef.current && sliderRef.current) {
            const { width } = viewPortRef.current.getBoundingClientRect();
            setViewPortWidth(width);
        }
    }, [viewPortWidth, windowWidth]);

    const isMobile = useBreakpoint("down", "sm");
    const showAsSlider: boolean = tyres.length > 3 || isMobile;

    // Retrieve slider props.
    const sliderProps = useSlider(
        tyres.length * (isMobile ? viewPortWidth : viewPortWidth / 3),
        viewPortWidth,
        isMobile ? viewPortWidth : viewPortWidth / 3,
    );
    const { enableCssTransition, rightArrowEnabled, leftArrowEnabled, rightArrowClickPage, leftArrowClickPage } =
        sliderProps;
    const { startTouchDrag, startMouseDrag, enableDrag, sliderX } = sliderProps;
    const { isScrolling, totalSliderWidth, onTransitionEnd } = sliderProps;

    return (
        <Styles.TyreInfoBody>
            {showAsSlider && (
                <Styles.SliderButton show={leftArrowEnabled} alignment="left" onClick={leftArrowClickPage}>
                    <Icon variant="chevron-left" />
                </Styles.SliderButton>
            )}
            <Styles.Slider hasSliderButtons={showAsSlider}>
                <Styles.SliderViewport ref={viewPortRef}>
                    <Styles.Track
                        ref={sliderRef}
                        width={totalSliderWidth}
                        left={enableDrag ? sliderX : 0}
                        enableTransition={enableDrag ? enableCssTransition : false}
                        onTransitionEnd={onTransitionEnd}
                        onTouchStart={enableDrag ? startTouchDrag : undefined}
                        onMouseDown={enableDrag ? startMouseDrag : undefined}
                        onClick={(event) => {
                            // Prevent the click from working when the slider is scrolling.
                            if (isScrolling.current) {
                                event.preventDefault();
                                event.stopPropagation();
                            }
                        }}
                    >
                        {tyres.map((tyreCode) => (
                            <Styles.ImageWrapper width={isMobile ? viewPortWidth : viewPortWidth / 3} key={tyreCode}>
                                {!tyreLabelError && (
                                    <Styles.Image
                                        src={getTyreLabelsUrl(settings, tyreCode)}
                                        onError={() => setTyreLabelError(true)}
                                        alt=""
                                    />
                                )}
                                {tyreLabelError && (
                                    <div>
                                        {tyreInfoLabels.tyreInfoFallbackDescription}
                                        <Button
                                            variant="link"
                                            href={`${settings.tyreLabelFallbackService}${tyreCode}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {tyreInfoLabels.tyreInfoFallback}
                                        </Button>
                                    </div>
                                )}
                            </Styles.ImageWrapper>
                        ))}
                    </Styles.Track>
                </Styles.SliderViewport>
            </Styles.Slider>
            {showAsSlider && (
                <Styles.SliderButton show={rightArrowEnabled} alignment="right" onClick={rightArrowClickPage}>
                    <Icon variant="chevron-right" />
                </Styles.SliderButton>
            )}
        </Styles.TyreInfoBody>
    );
};

export default TyreSlider;
